"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { CapturedNetworkRequest } from "posthog-js";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";
import { sanitizeAnalyticsEvent } from "modules/common/analytics/pathSanitizer";
import { getLoadedPostHog, loadPostHog } from "utils/posthog-client";

interface PostHogProviderProps {
  children: ReactNode;
}

interface PostHogState {
  hasAnalyticsConsent: boolean;
  isInitialized: boolean;
}

const PostHogStateContext = createContext<PostHogState>({
  hasAnalyticsConsent: false,
  isInitialized: false,
});

export function usePostHogState(): PostHogState {
  return useContext(PostHogStateContext);
}

const SENSITIVE_QUERY_PARAMETER =
  /^(?:access_?token|refresh_?token|token|auth(?:orization)?|api_?key|client_?secret|secret|password|passwd|credentials?|email|phone|ticket|user_?id|username|session_?id|distinct_?id)$/i;
const POSTHOG_UI_HOST = "https://eu.posthog.com";

function getTermlyAnalyticsConsent(): boolean {
  return window.Termly?.getConsentState()?.analytics ?? false;
}

function redactSensitiveQueryParameters(
  request: CapturedNetworkRequest
): CapturedNetworkRequest {
  try {
    const url = new URL(request.name, window.location.origin);
    let wasRedacted = false;

    for (const [name, value] of url.searchParams.entries()) {
      const looksLikeJwt = /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value);
      if (SENSITIVE_QUERY_PARAMETER.test(name) || looksLikeJwt) {
        url.searchParams.set(name, "[REDACTED]");
        wasRedacted = true;
      }
    }

    return wasRedacted ? { ...request, name: url.toString() } : request;
  } catch {
    return request;
  }
}

export default function PostHogProvider({ children }: PostHogProviderProps) {
  const { userId, appUser, isUserLoaded } = useAppUser();
  const projectToken = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  const isEnabled =
    process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true" ||
    process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "1";

  const identifiedUserId = useRef<string | null>(null);
  const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleConsentChange = useCallback(() => {
    setHasAnalyticsConsent(getTermlyAnalyticsConsent());
  }, []);

  useEffect(() => {
    window.addEventListener("termly:consent-change", handleConsentChange);
    setHasAnalyticsConsent(getTermlyAnalyticsConsent());

    return () => {
      window.removeEventListener("termly:consent-change", handleConsentChange);
    };
  }, [handleConsentChange]);

  useEffect(() => {
    let isCancelled = false;

    if (!isEnabled || !projectToken || !apiHost) {
      return;
    }

    if (!hasAnalyticsConsent) {
      const posthog = getLoadedPostHog();
      if (posthog?.__loaded) {
        posthog.stopSessionRecording();
        posthog.opt_out_capturing();
      }
      setIsInitialized(false);
      return;
    }

    void loadPostHog()
      .then((posthog) => {
        if (isCancelled) {
          return;
        }

        if (!posthog.__loaded) {
          posthog.init(projectToken, {
            api_host: apiHost,
            ui_host: POSTHOG_UI_HOST,
            defaults: "2026-05-30",
            capture_exceptions: true,
            person_profiles: "identified_only",
            enable_recording_console_log: true,
            capture_performance: true,
            before_send: sanitizeAnalyticsEvent,
            session_recording: {
              maskAllInputs: true,
              recordHeaders: false,
              recordBody: false,
              maskCapturedNetworkRequestFn: redactSensitiveQueryParameters,
            },
          });

          // A previous consent revocation is persisted by PostHog. Termly is
          // the source of truth, so restore capture when consent is granted.
          if (posthog.has_opted_out_capturing()) {
            posthog.opt_in_capturing({ captureEventName: false });
            posthog.capture("$pageview");
            posthog.startSessionRecording();
          }
        } else {
          posthog.opt_in_capturing({ captureEventName: false });
          posthog.startSessionRecording();
        }

        setIsInitialized(true);
      })
      .catch(() => {
        if (!isCancelled) {
          setIsInitialized(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [apiHost, hasAnalyticsConsent, isEnabled, projectToken]);

  useEffect(() => {
    if (!isInitialized || !hasAnalyticsConsent || !isUserLoaded) {
      return;
    }

    const posthog = getLoadedPostHog();
    if (!posthog?.__loaded) {
      return;
    }

    if (userId && appUser) {
      posthog.identify(userId, {
        name: appUser.username || "Unknown",
        username: appUser.username || undefined,
        user_id: userId,
      });
      identifiedUserId.current = userId;
      return;
    }

    if (
      !userId &&
      (identifiedUserId.current || posthog.get_property("$user_id"))
    ) {
      posthog.reset();
      identifiedUserId.current = null;
    }
  }, [appUser, hasAnalyticsConsent, isInitialized, isUserLoaded, userId]);

  return (
    <PostHogStateContext.Provider
      value={{ hasAnalyticsConsent, isInitialized }}
    >
      {children}
    </PostHogStateContext.Provider>
  );
}
